<template>
  <settings-chrome
    kicker="Settings"
    title="Business details"
    sub="Used on invoices and as a starting point for your shopfront."
    form-id="business-settings-form"
    :dirty="dirty"
    :saving="isSaving"
    @discard="revert"
    @allow-leave="dirty = false"
  >
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        id="business-settings-form"
        class="st-body"
        @submit.prevent="handleSubmit(submit)"
      >
        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Your business</h2>
              <p class="st-lead">
                Name, email and logo on invoices.
              </p>
            </div>
          </div>
          <div class="st-identity">
            <shop-logo-input v-model="model.logoDownloadUrl" @error="onLogoError" />
            <div class="st-identity-fields">
              <label class="st-field">
                Business name
                <ValidationProvider rules="required|min:2" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.name"
                    placeholder="The name on invoices"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="st-field">
                Email
                <p class="st-hint">Customers can reply here.</p>
                <ValidationProvider rules="required|email" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    type="email"
                    v-model="model.emailAddress"
                    placeholder="hello@business.ie"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
          </div>
        </section>

        <section class="st-card">
          <div class="st-card-head">
            <div>
              <h2>Address</h2>
              <p class="st-lead">Printed on invoices.</p>
            </div>
          </div>
          <div class="st-stack">
            <label class="st-field">
              Address line 1
              <ValidationProvider rules="required" v-slot="{ errors }">
                <fg-input
                  class="mb-0"
                  v-model="model.address.line1"
                  :placeholder="$t('message.addressLine1Placeholder')"
                  :error="errors[0]"
                />
              </ValidationProvider>
            </label>
            <label class="st-field">
              Address line 2
              <fg-input
                class="mb-0"
                v-model="model.address.line2"
                :placeholder="$t('message.addressLine2Placeholder')"
              />
            </label>
            <div class="st-pair">
              <label class="st-field">
                Town / district
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.district"
                    :placeholder="$t('message.addressDistrictPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="st-field">
                County / region
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.region"
                    :placeholder="$t('message.addressRegionPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
            <div class="st-pair">
              <label class="st-field">
                Country
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.country"
                    :placeholder="$t('message.addressCountryPlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
              <label class="st-field">
                Eircode / postcode
                <ValidationProvider rules="required" v-slot="{ errors }">
                  <fg-input
                    class="mb-0"
                    v-model="model.address.postcode"
                    :placeholder="$t('message.addressPostcodePlaceholder')"
                    :error="errors[0]"
                  />
                </ValidationProvider>
              </label>
            </div>
          </div>
        </section>
      </form>
    </ValidationObserver>
  </settings-chrome>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { DEFAULT_SETTINGS } from "@/store/userSettings";
import SettingsChrome from "@/components/settings/SettingsChrome";
import ShopLogoInput from "@/components/ShopLogoInput";

function mergeCompany(company) {
  const base = company || {};
  return {
    ...DEFAULT_SETTINGS.company,
    ..._.cloneDeep(base),
    address: {
      ...DEFAULT_SETTINGS.company.address,
      ...(base.address || {})
    }
  };
}

export default {
  components: { SettingsChrome, ShopLogoInput },
  data() {
    return {
      isSaving: false,
      dirty: false,
      hydrating: false,
      model: mergeCompany({})
    };
  },
  computed: mapGetters({ userSettings: "userSettings" }),
  watch: {
    userSettings: {
      immediate: true,
      deep: true,
      handler() {
        if (this.dirty || this.isSaving) return;
        this.resetModel();
      }
    },
    model: {
      deep: true,
      handler() {
        if (this.hydrating) return;
        this.dirty = true;
      }
    }
  },
  created() {
    this.fetchUserSettings();
  },
  methods: {
    ...mapActions(["fetchUserSettings", "updateUserSettings", "updateInvoiceLogo"]),
    resetModel() {
      this.hydrating = true;
      const company = (this.userSettings && this.userSettings.company) || {};
      this.model = mergeCompany(company);
      this.dirty = false;
      this.$nextTick(() => {
        this.hydrating = false;
      });
    },
    revert() {
      this.dirty = false;
      this.resetModel();
    },
    onLogoError({ message }) {
      this.$notify({
        type: "danger",
        title: this.$t("message.error"),
        text: message
      });
    },
    async submit() {
      this.isSaving = true;
      try {
        const company = { ...this.model };
        if (
          company.logoDownloadUrl &&
          String(company.logoDownloadUrl).startsWith("data:")
        ) {
          await this.updateInvoiceLogo(company.logoDownloadUrl);
          company.logoDownloadUrl =
            this.userSettings.company &&
            this.userSettings.company.logoDownloadUrl;
        }
        await this.updateUserSettings({
          ...this.userSettings,
          company
        });
        this.dirty = false;
        this.resetModel();
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.saveSettingsSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.saveSettingsFailure") +
            ` ${(error && error.message) || ""}`.trim()
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>
